import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../../context/AuthContext';
import { verifyMFARequest } from '../../../modules/SignIn/api';

const CODE_LENGTH = 6;
const emptyCode = () => Array(CODE_LENGTH).fill('');
const inputId = index => `mfa-code-input-signup-${index}`;

const focusInput = index => document.getElementById(inputId(index))?.focus();

// MFA step for the Google sign-up path: the backend asks for a second factor
// before it hands out an access token.
export const useMfaVerification = () => {
	const navigate = useNavigate();
	const { setIsAuth } = useAuth();

	const [mfaData, setMfaData] = useState(null);
	const [codeInputs, setCodeInputs] = useState(emptyCode);
	const [error, setError] = useState('');
	const [isLoading, setIsLoading] = useState(false);

	const code = codeInputs.join('');

	const open = data => {
		setMfaData(data);
		setCodeInputs(emptyCode());
	};

	const close = () => {
		setMfaData(null);
		setCodeInputs(emptyCode());
		setError('');
	};

	const handleInputChange = (index, value) => {
		const digit = value.replace(/\D/g, '').slice(0, 1);
		const next = [...codeInputs];
		next[index] = digit;
		setCodeInputs(next);
		setError('');
		if (digit && index < CODE_LENGTH - 1) focusInput(index + 1);
	};

	const handleKeyDown = (index, e) => {
		if (e.key === 'Backspace' && !codeInputs[index] && index > 0) {
			focusInput(index - 1);
		}
	};

	const handlePaste = e => {
		e.preventDefault();
		const pasted = e.clipboardData
			.getData('text')
			.replace(/\D/g, '')
			.slice(0, CODE_LENGTH);
		if (!pasted) return;

		const next = emptyCode();
		for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
		setCodeInputs(next);
		setError('');
		focusInput(Math.min(pasted.length, CODE_LENGTH - 1));
	};

	const verify = async e => {
		e.preventDefault();
		setIsLoading(true);
		setError('');

		if (!/^\d{6}$/.test(code)) {
			setError('Please enter a valid 6-digit code');
			setIsLoading(false);
			return;
		}

		try {
			const result = await verifyMFARequest(
				mfaData.temp_token,
				code,
				mfaData.mfa_type,
			);

			if (result?.response) {
				const detail = result.response?.data?.detail;
				setError(typeof detail === 'string' ? detail : 'Invalid MFA code');
				return;
			}

			if (result?.access_token) {
				window.localStorage.setItem('accessToken', result.access_token);
				window.localStorage.setItem(
					'authProvider',
					mfaData.authProvider || 'email',
				);
				if (result.mfa_session_token) {
					window.localStorage.setItem(
						'mfaSessionToken',
						result.mfa_session_token,
					);
				}

				setIsAuth(true);
				navigate('/overview');
			} else {
				setError('Login failed. Please try again.');
			}
		} catch (err) {
			console.error(err);
			setError('Verification failed. Please try again.');
		} finally {
			setIsLoading(false);
		}
	};

	return {
		mfaData,
		codeInputs,
		code,
		error,
		isLoading,
		inputId,
		open,
		close,
		verify,
		handleInputChange,
		handleKeyDown,
		handlePaste,
	};
};
